import React from "react";
import { WhatsappIcon2, SmsIcon, EmailIcon, RcsIcon2 } from "../../../assets/icon";
import { ChannelPerformanceStats } from "../../../views/marketing-pages/cloud-dashboard/types";

interface ChannelPerformanceProps {
  channels: {
    email: ChannelPerformanceStats;
    rcs: ChannelPerformanceStats;
    sms: ChannelPerformanceStats;
    whatsapp: ChannelPerformanceStats;
  } | null;
}

const CHANNEL_META: Array<{ key: "sms" | "whatsapp" | "email" | "rcs"; label: string; icon: React.ReactNode }> = [
  { key: "sms", label: "SMS", icon: <SmsIcon className="w-5 h-5" /> },
  { key: "whatsapp", label: "WhatsApp", icon: <WhatsappIcon2 className="w-5 h-5" /> },
  { key: "email", label: "Email", icon: <EmailIcon className="w-5 h-5" /> },
  { key: "rcs", label: "RCS", icon: <RcsIcon2 className="w-5 h-5" /> },
];

const pct = (n?: number) => `${(n ?? 0).toFixed(1)}%`;

// "Channel Performance" — a per-channel breakdown of the same campaign
// analytics the CPaaS dashboard's top stat cards already summarise, now that
// /reports/analytics/overview returns a `channels` object instead of just
// flat totals. Table layout (rather than 4 separate cards) so the channels
// stay easy to compare side by side.
const ChannelPerformance: React.FC<ChannelPerformanceProps> = ({ channels }) => {
  if (!channels) return null;

  return (
    <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden mb-8">
      <div className="px-6 pt-6 pb-4">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-gray-100">Channel Performance</h3>
        <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
          Campaign delivery and engagement, broken down by communication channel.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              {["Channel", "Campaigns", "Sent", "Delivered", "Rejected", "Opened", "Clicks", "Replies"].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CHANNEL_META.map(({ key, label, icon }) => {
              const c = channels[key];
              if (!c) return null;
              return (
                <tr key={key} className="border-b border-slate-50 dark:border-gray-800/60 last:border-b-0">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                        {icon}
                      </span>
                      <span className="text-sm font-medium text-slate-800 dark:text-gray-100">{label}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                    {c.total_campaigns.toLocaleString()}
                    <span className="text-xs text-slate-400 dark:text-gray-500 ml-1">({c.active_campaigns} active)</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                    {c.sent.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <span className="text-slate-700 dark:text-gray-300">{c.delivered.toLocaleString()}</span>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 ml-1.5">{pct(c.delivered_rate)}</span>
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <span className="text-slate-700 dark:text-gray-300">{c.failed.toLocaleString()}</span>
                    <span className="text-xs text-rose-600 dark:text-rose-400 ml-1.5">{pct(c.failed_rate)}</span>
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <span className="text-slate-700 dark:text-gray-300">{c.opened.toLocaleString()}</span>
                    <span className="text-xs text-slate-400 dark:text-gray-500 ml-1.5">{pct(c.opened_rate)}</span>
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <span className="text-slate-700 dark:text-gray-300">{c.total_clicks.toLocaleString()}</span>
                    <span className="text-xs text-slate-400 dark:text-gray-500 ml-1.5">{pct(c.clicked_rate)}</span>
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <span className="text-slate-700 dark:text-gray-300">{c.replies.toLocaleString()}</span>
                    <span className="text-xs text-slate-400 dark:text-gray-500 ml-1.5">{pct(c.reply_rate)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelPerformance;
